import styled from '@emotion/styled'
import { SearchOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Modal, Spin, Table, Tabs, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FC, useMemo, useState } from 'react'
import { backendUrl } from 'config'
import { useGeneralStockPicker, useSkuStockPicker } from 'hooks/useProductStockPicker'
import { useListProductRecipes } from 'hooks/useProductRecipe'
import type { ProductStockPickerRow } from 'services/interface/productStockPicker.interface'
import type { RecipeItemRefType } from 'services/interface/productRecipe.interface'
import StockImageThumb from '../StockImageThumb'
import {
  MASTER_STOCK_LABEL,
  NESTED_SET_LABEL,
  SET_TAG_LABEL,
} from './bundleLabels'

export type PickedStockItem = {
  ref_id: string
  ref_type: RecipeItemRefType
  ref_label: string
  ref_stock_type?: 'sku' | 'general'
  ref_stock_type_label?: string
  ref_image_url?: string | null
  ref_description?: string | null
  ref_balance?: number
}

interface RecipeStockPickerModalProps {
  open: boolean
  onClose: () => void
  onPick: (item: PickedStockItem) => void
  exclude_ref_ids?: string[]
  exclude_recipe_id?: string | null
}

const RecipeStockPickerModal: FC<RecipeStockPickerModalProps> = ({
  open,
  onClose,
  onPick,
  exclude_ref_ids = [],
  exclude_recipe_id,
}) => {
  const [tab, setTab] = useState<'general' | 'sku' | 'recipe'>('general')
  const [search_input, set_search_input] = useState('')
  const [applied_search, set_applied_search] = useState('')

  const generalQuery = useGeneralStockPicker(
    { search: applied_search || undefined, limit: 100, is_active: true },
    open && tab === 'general'
  )
  const skuQuery = useSkuStockPicker(
    { search: applied_search || undefined, limit: 100, is_active: true },
    open && tab === 'sku'
  )
  const recipeQuery = useListProductRecipes(
    { search: applied_search || undefined, limit: 100, is_active: true },
    open && tab === 'recipe'
  )

  const excludeSet = useMemo(() => new Set(exclude_ref_ids), [exclude_ref_ids])

  const stock_rows = useMemo(() => {
    const q = tab === 'general' ? generalQuery : skuQuery
    const list = q.data?.data ?? []
    return list.filter((r) => !excludeSet.has(r.id))
  }, [tab, generalQuery.data?.data, skuQuery.data?.data, excludeSet])

  const recipe_rows = useMemo(() => {
    const list = recipeQuery.data?.data ?? []
    return list.filter(
      (r) => r.id !== exclude_recipe_id && !excludeSet.has(r.id)
    )
  }, [recipeQuery.data?.data, exclude_recipe_id, excludeSet])

  const renderPickerThumb = (url?: string | null, alt?: string) => (
    <PickerThumbBox>
      <StockImageThumb src={url} alt={alt ?? ''} />
    </PickerThumbBox>
  )

  const stock_columns: ColumnsType<ProductStockPickerRow> = [
    {
      title: '',
      width: 52,
      render: (_, row) => renderPickerThumb(row.image_url, row.ref_label),
    },
    {
      title: 'รายการ',
      dataIndex: 'ref_label',
      ellipsis: true,
      render: (_v, row) => (
        <div>
          <div>{row.ref_label}</div>
          {row.description ? (
            <SecondaryLine>{row.description}</SecondaryLine>
          ) : null}
        </div>
      ),
    },
    {
      title: 'ประเภท',
      width: 88,
      render: (_, row) => (
        <Tag color={row.type === 'general' ? 'blue' : 'purple'}>
          {row.type === 'general' ? MASTER_STOCK_LABEL : 'SKU'}
        </Tag>
      ),
    },
    {
      title: 'คงเหลือ',
      width: 88,
      align: 'right',
      render: (_, row) => row.stock_balance.toLocaleString(),
    },
    {
      title: '',
      width: 88,
      render: (_, row) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            onPick({
              ref_id: row.id,
              ref_type: 'product_stock',
              ref_label: row.ref_label,
              ref_stock_type: row.type,
              ref_stock_type_label:
                row.type === 'general' ? MASTER_STOCK_LABEL : 'สต็อก SKU',
              ref_image_url: row.image_url,
              ref_description: row.description,
              ref_balance: row.stock_balance,
            })
            onClose()
          }}
        >
          เลือก
        </Button>
      ),
    },
  ]

  const recipeImageUrl = (row: (typeof recipe_rows)[0]) =>
    row.image_url ??
    (row.images?.[0] ? `${backendUrl}images/${row.images[0]}` : null)

  const recipe_columns: ColumnsType<(typeof recipe_rows)[0]> = [
    {
      title: '',
      width: 52,
      render: (_, row) => renderPickerThumb(recipeImageUrl(row), row.name),
    },
    {
      title: `ชื่อ${SET_TAG_LABEL}`,
      dataIndex: 'name',
      ellipsis: true,
    },
    {
      title: 'ส่วนประกอบ',
      width: 88,
      align: 'center',
      render: (_, row) => row.item_count ?? 0,
    },
    {
      title: '',
      width: 88,
      render: (_, row) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            onPick({
              ref_id: row.id,
              ref_type: 'product_recipe',
              ref_label: row.name,
              ref_stock_type_label: NESTED_SET_LABEL,
              ref_image_url: recipeImageUrl(row),
            })
            onClose()
          }}
        >
          เลือก
        </Button>
      ),
    },
  ]

  const handleSearch = () => {
    set_applied_search(search_input.trim())
  }

  const loading =
    tab === 'recipe'
      ? recipeQuery.isPending || recipeQuery.isFetching
      : tab === 'general'
        ? generalQuery.isPending || generalQuery.isFetching
        : skuQuery.isPending || skuQuery.isFetching

  const empty =
    tab === 'recipe' ? recipe_rows.length === 0 : stock_rows.length === 0

  return (
    <Modal
      title="เลือกส่วนประกอบ"
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
      centered
      afterClose={() => {
        set_search_input('')
        set_applied_search('')
        setTab('general')
      }}
    >
      <SearchRow>
        <Input
          placeholder={
            tab === 'recipe'
              ? `ค้นหาชื่อ${SET_TAG_LABEL}`
              : 'ค้นหาชื่อ / รายละเอียด / สินค้า / SKU'
          }
          value={search_input}
          onChange={(e) => set_search_input(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          ค้นหา
        </Button>
      </SearchRow>

      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as 'general' | 'sku' | 'recipe')}
        items={[
          { key: 'general', label: MASTER_STOCK_LABEL },
          { key: 'sku', label: 'สต็อก SKU' },
          { key: 'recipe', label: NESTED_SET_LABEL },
        ]}
      />

      {loading ? (
        <SpinWrap>
          <Spin />
        </SpinWrap>
      ) : empty ? (
        <Empty description="ไม่พบรายการ" />
      ) : tab === 'recipe' ? (
        <Table
          size="small"
          rowKey="id"
          columns={recipe_columns}
          dataSource={recipe_rows}
          pagination={false}
          scroll={{ y: 360 }}
        />
      ) : (
        <Table
          size="small"
          rowKey="id"
          columns={stock_columns}
          dataSource={stock_rows}
          pagination={false}
          scroll={{ y: 360 }}
        />
      )}
    </Modal>
  )
}

export default RecipeStockPickerModal

const SearchRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`

const SpinWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px;
`

const SecondaryLine = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 2px;
`

const PickerThumbBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  flex-shrink: 0;
`
